import EventBus from '@/helpers/event-bus';
import jwtParser from '@/helpers/jwtParser';
import UserService from '@/services/User/UserService';
import CompanyService from '@/services/Companies/CompanyService';
import constances from '@/configs/constances';
import { setStoreValue } from '@/store/modules/storeHelpers';


const state = {
    user: {},
    companyFirstUser: {},
    rememberMe: false,
    users: [],
    token: localStorage.getItem('token') || '',
    refreshToken: null,
    userAttachedCompanies: [],
    sectorAssociation: {},
};

const actions = {
    async login({ commit, dispatch }, credentials) {
        try {
            const loginResponse = await UserService.login(credentials);
            const { data } = loginResponse;

            if (data && data.status === 1) {
                await commit('storeToken', data.jwt);
                await dispatch('getUserInfo');
                return true;
            }
        } catch (error) {
            EventBus.$emit('snotifyError', 'User login error.');
        }
    },
    logout({ commit }) {
        commit('storeToken', '');
        commit('clearUser');
        location.reload(true);
    },
    async getUserSectorAssociation({ commit }, userId) {
        try {
            const { data } = await UserService.sectorAssociation(userId);
            if (data) {
                commit('setStoreValue', { key: 'sectorAssociation', value: data });
            }
        } catch (error) {
            console.error(error);
        }
    },
    async getUsersList({ commit }) {
        try {
            const { data } = await UserService.usersList();
            if (data) {
                commit('setUsers', data);
            }
        } catch (error) {
            console.error(error);
        }
    },
    async updateUser({ dispatch }, data) {
        let isError = false;
        try {
            await UserService.update(data);
            EventBus.$emit('snotifySuccess', 'User updated.');
            await dispatch('getUserInfo');
        } catch (error) {
            EventBus.$emit('snotifyError', 'User updated error.');
            isError = true;
        }
        return isError;
    },
    async adminEditUserBaseData(_, user) {
        try {
            await UserService.adminEditUserBaseData(user);
            EventBus.$emit('snotifySuccess', 'User updated.');
        } catch (error) {
            EventBus.$emit('snotifyError', 'User updated error.');
        }
    },
    async adminEditAcceptUser({ dispatch }, userId) {
        try {
            await UserService.adminEditAcceptUser(userId);
            EventBus.$emit('snotifySuccess', 'User accepted.');
            dispatch('getUsersList');
        } catch (error) {
            EventBus.$emit('snotifyError', 'User accepted error.');
        }
    },
    async adminEditUnblockLoginUser({ dispatch }, userId) {
        try {
            await UserService.adminEditUnblockLoginUser(userId);
            EventBus.$emit('snotifySuccess', 'User unblocked.');
            dispatch('getUsersList');
        } catch (error) {
            EventBus.$emit('snotifyError', 'User unblocked error.');
        }
    },
    async adminEditUserAttachCompany(_, attachCompanyData) {
        try {
            const { data } = await UserService.adminEditUserAttachCompany(attachCompanyData);
            if (data && data.status === 1) {
                EventBus.$emit('snotifySuccess', 'Company data attached');
            }
        } catch (error) {
            EventBus.$emit('snotifyError', 'Company data attach error.');
        }
    },
    async getUserInfo({ commit }) {
        try {
            const { data } = await UserService.info();

            if (data) {
                commit('storeUser', data);
            }
        } catch (error) {
            console.error(error);
        }
    },
    async getUserAttachedCompany({ commit }, userId) {
        try {
            if (userId) {
                const { data } = await UserService.getUserAttachedCompany(userId);
                commit('setUserAttachedCompanies', data);
                return { userCompaniesId: data };
            }
        } catch (error) {
            console.error(error);
        }
    },
    async getCompanyFirstUser({ commit }, companyId) {
        const { data } = await CompanyService.getCompanyFirstUser(companyId);
        if (data && data.data) {
            commit('setStoreValue', { key: 'companyFirstUser', value: data.data });
        }
      },
};

const mutations = {
    setStoreValue,
    storeUser(state, user) {
        state.user = user;
        localStorage.setItem('user', JSON.stringify(user));
    },
    clearUser(state) {
        state.user = null;
        localStorage.removeItem('user');
    },
    storeToken(state, token) {
        state.token = token;
        localStorage.setItem('token', token);
    },
    setUsers(state, data) {
        state.users = data;
    },
    setUserAttachedCompanies(state, data) {
        state.userAttachedCompanies = data;
    },
};

const getters = {
    user(state) {
        return state.user;
    },
    companyFirstUser(state) {
        return state.companyFirstUser;
    },
    rememberMe(state) {
        return state.rememberMe;
    },
    token(state) {
        return state.token;
    },
    users(state) {
        return state.users;
    },
    isAdmin(state) {
        return state.user ? state.user.role_id === constances.roles.ADMIN : false;
    },
    isRecycler(state) {
        return state.user ? state.user.role_id === constances.roles.RECYCLER : false;
    },
    isAuditor(state) {
        return state.user ? state.user.role_id === constances.roles.AUDITOR : false;
    },
    isRepresentative(state) {
        return state.user ? state.user.role_id === constances.roles.REPRESENTATIVE : false;
    },
    isSectorAssociationRepresentative(state) {
        return state.user ? state.user.role_id === constances.roles.SECTOR_ASSOCIATION_REPRESENTATIVE : false;
    },
    isLoggedIn(state) {
        if (jwtParser(state.token) === null) {
            return false;
        }
        const nowInSeconds = new Date().getTime() / 1000;
        if (nowInSeconds > jwtParser(state.token).exp) {
            return false;
        }
        return !!state.token;
    },
    getLoggedInUserId() {
        const user = JSON.parse(localStorage.getItem('user'));
        if (user && user.id) {
            return user.id;
        }

        return 0;
    },
    getWeightUnit(state) {
        if (state.user) {
            return state.user.tonnes ? 'ton' : 'kg';
        }
        return 'kg';
    }
};

export default {
    state,
    mutations,
    actions,
    getters,
    namespaced: true
};
